import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";

import api from "../../services/api";

const STATUS_LABEL = { open: "Aberto", pending: "Aguardando", closed: "Resolvido" };
const STATUS_COLOR = { open: "primary", pending: "warning", closed: "default" };

const GlobalSearchDialog = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setQuery("");
      setContacts([]);
      setTickets([]);
      setFocusedIdx(-1);
    }
  }, [open]);

  const search = useCallback((q) => {
    if (!q.trim() || q.trim().length < 2) {
      setContacts([]);
      setTickets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      api.get("/contacts", { params: { searchParam: q, limit: 10 } }).catch(() => ({ data: { contacts: [] } })),
      api.get("/tickets", { params: { searchParam: q, showAll: true, limit: 10 } }).catch(() => ({ data: { tickets: [] } })),
    ]).then(([cRes, tRes]) => {
      setContacts(cRes.data.contacts || []);
      setTickets(tRes.data.tickets || []);
      setFocusedIdx(-1);
    }).finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setFocusedIdx(-1);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(val), 300);
  };

  const handleClear = () => {
    setQuery("");
    setContacts([]);
    setTickets([]);
    setFocusedIdx(-1);
  };

  const goToTicket = (ticketId) => {
    onClose();
    navigate(`/tickets/${ticketId}`);
  };

  const goToContact = (contactId) => {
    onClose();
    navigate(`/contacts?id=${contactId}`);
  };

  // Flat list for keyboard navigation: contacts first, then tickets
  const allResults = [
    ...contacts.map(c => ({ type: "contact", id: c.id, action: () => goToContact(c.id) })),
    ...tickets.map(t => ({ type: "ticket", id: t.id, action: () => goToTicket(t.id) })),
  ];

  const handleKeyDown = (e) => {
    if (allResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIdx(prev => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIdx(prev => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && focusedIdx >= 0) {
      e.preventDefault();
      allResults[focusedIdx].action();
    }
  };

  const hasResults = contacts.length > 0 || tickets.length > 0;
  let resultOffset = 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" disableRestoreFocus>
      <DialogContent style={{ padding: "12px 16px" }}>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Buscar contatos, tickets… (mín. 2 caracteres)"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? <CircularProgress size={16} /> : <SearchIcon style={{ color: "#8A9096" }} />}
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {hasResults && (
          <List dense style={{ marginTop: 8 }}>
            {contacts.length > 0 && (
              <>
                <Typography variant="caption" style={{ padding: "4px 0", display: "block", color: "#8A9096", fontWeight: 600 }}>
                  CONTATOS
                </Typography>
                {contacts.map((c, i) => {
                  const idx = resultOffset + i;
                  return (
                    <ListItemButton
                      key={c.id}
                      onClick={() => goToContact(c.id)}
                      selected={focusedIdx === idx}
                      style={{ borderRadius: 6 }}
                    >
                      <ListItemAvatar>
                        <Avatar src={c.profilePicUrl} style={{ width: 32, height: 32 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={c.name}
                        secondary={c.number}
                        primaryTypographyProps={{ style: { fontSize: 14 } }}
                        secondaryTypographyProps={{ style: { fontSize: 12 } }}
                      />
                    </ListItemButton>
                  );
                })}
                {(() => { resultOffset += contacts.length; return null; })()}
                {tickets.length > 0 && <Divider style={{ margin: "8px 0" }} />}
              </>
            )}
            {tickets.length > 0 && (
              <>
                <Typography variant="caption" style={{ padding: "4px 0", display: "block", color: "#8A9096", fontWeight: 600 }}>
                  TICKETS
                </Typography>
                {tickets.map((t, i) => {
                  const idx = resultOffset + i;
                  return (
                    <ListItemButton
                      key={t.id}
                      onClick={() => goToTicket(t.id)}
                      selected={focusedIdx === idx}
                      style={{ borderRadius: 6 }}
                    >
                      <ListItemAvatar>
                        <Avatar src={t.contact?.profilePicUrl} style={{ width: 32, height: 32 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={t.contact?.name}
                        secondary={`#${t.id} · ${t.queue?.name || "Sem fila"}`}
                        primaryTypographyProps={{ style: { fontSize: 14 } }}
                        secondaryTypographyProps={{ style: { fontSize: 12 } }}
                      />
                      <Chip
                        label={STATUS_LABEL[t.status] || t.status}
                        color={STATUS_COLOR[t.status] || "default"}
                        size="small"
                        style={{ marginLeft: 8 }}
                      />
                    </ListItemButton>
                  );
                })}
              </>
            )}
          </List>
        )}

        {!loading && query.trim().length >= 2 && !hasResults && (
          <Typography variant="body2" style={{ textAlign: "center", color: "#8A9096", padding: "16px 0" }}>
            Nenhum resultado para "{query}"
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearchDialog;
